import { useEffect, useMemo, useState, useCallback } from 'react';
import { getTelegram } from '../telegram';
import { registerClient, getDirectoryShops, claimTelegramLink } from '../api';
import type { DirectoryShop, RepresentativeType } from '../types';

interface Props {
  onSuccess: (type: RepresentativeType) => void;
  onBack: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1.5px solid var(--tg-hint)',
  background: 'var(--tg-secondary-bg)',
  color: 'var(--tg-text)',
  fontSize: 15,
  fontFamily: 'inherit',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--tg-hint)',
  marginBottom: 4,
  display: 'block',
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

const DELIVERY_LABEL: Record<string, string> = {
  city: 'Городская доставка',
  intercity: 'Межгородская доставка',
};

export default function RegistrationPage({ onSuccess, onBack }: Props) {
  // Этап 20: первый шаг — выбор типа представителя. null = показываем экран выбора.
  //  • 'greenhouse' — представитель GreenHouse (видит блок «Ваша точка»);
  //  • 'supplier'   — представитель поставщика (точку не выбирает).
  const [repType, setRepType] = useState<RepresentativeType | null>(null);

  // Этап 15.1: контактное лицо — отдельный блок сверху, все 4 поля обязательны.
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Этап W1 (вариант B): пароль для резервного входа через сайт. «Повтор пароля»
  // проверяется только на фронте (на бэкенд уходит один пароль).
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  // Этап 19.3: «пожелание» по точке — НЕОБЯЗАТЕЛЬНО (только для GreenHouse).
  const [shops, setShops] = useState<DirectoryShop[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopsError, setShopsError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  // Свободный текст: «не нашёл свою точку» — описывает её словами.
  const [wishText, setWishText] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Этап 29 (152-ФЗ): согласие на обработку ПДн. Не предзаполнено — пользователь
  // ставит галочку сам, иначе кнопка «Отправить» заблокирована.
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Простая проверка email: должен содержать @ и точку. Полноценную валидацию
  // оставляем на бэкенд — здесь только защита от очевидных опечаток.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // Телефон: 5-32 символа (бэкенд проверит формат строже).
  const phoneValid = phone.trim().length >= 5;
  // Этап W1: пароль ≥ 8 символов (как на бэкенде) и совпадает с повтором.
  const passwordValid = password.length >= 8;
  const passwordsMatch = password === password2;
  // Для отправки достаточно контактных данных — точка необязательна.
  // Этап 29: + обязательное согласие на обработку ПДн.
  const canSubmit =
    repType !== null &&
    !!lastName.trim() &&
    !!firstName.trim() &&
    emailValid &&
    phoneValid &&
    passwordValid &&
    passwordsMatch &&
    privacyAccepted;

  const selectedShop = useMemo(
    () => shops.find(s => s.id === selectedShopId) ?? null,
    [shops, selectedShopId],
  );

  // Фильтрация справочника по названию/адресу. Список небольшой (~119), показываем
  // максимум 30 совпадений, чтобы не рендерить всё разом.
  const filteredShops = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? shops.filter(s => s.name.toLowerCase().includes(q) || s.address.toLowerCase().includes(q))
      : shops;
    return base.slice(0, 30);
  }, [shops, search]);

  useEffect(() => {
    getDirectoryShops()
      .then(({ data }) => setShops(data))
      .catch((e: Error) => setShopsError(e.message))
      .finally(() => setShopsLoading(false));
  }, []);

  // Кнопка «Назад»: с формы → возвращаемся к выбору типа; с экрана выбора → к каталогу.
  const handleBack = useCallback(() => {
    if (repType !== null) {
      setRepType(null);
      setError(null);
    } else {
      onBack();
    }
  }, [repType, onBack]);

  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(handleBack);
    return () => { tg.BackButton.offClick(handleBack); tg.BackButton.hide(); };
  }, [handleBack]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting || repType === null) return;
    setSubmitting(true);
    setError(null);
    try {
      await registerClient({
        representative_type: repType,
        last_name: lastName.trim(),
        first_name: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        // Этап W1 (вариант B): пароль для резервного web-входа.
        password,
        // Этап 29 (152-ФЗ): согласие на обработку ПДн.
        privacy_accepted: privacyAccepted,
        // Пожелание по точке — только для представителя GreenHouse.
        ...(repType === 'greenhouse'
          ? (selectedShopId
              ? { wished_shop_id: selectedShopId }
              : wishText.trim()
                ? { wished_shop_text: wishText.trim() }
                : {})
          : {}),
      });
      getTelegram()?.MainButton?.hideProgress();
      onSuccess(repType);
    } catch (e) {
      setError((e as Error).message);
      getTelegram()?.MainButton?.hideProgress();
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, submitting, repType, lastName, firstName, email, phone, password, privacyAccepted, selectedShopId, wishText, onSuccess]);

  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    // На экране выбора типа MainButton не нужен.
    if (repType === null) { tg.MainButton.hide(); return; }
    if (canSubmit && !submitting) {
      tg.MainButton.hideProgress();
      tg.MainButton.setText('Отправить заявку');
      tg.MainButton.show();
      tg.MainButton.onClick(handleSubmit);
    } else if (submitting) {
      tg.MainButton.showProgress(false);
    } else {
      tg.MainButton.hide();
    }
    return () => { tg.MainButton.offClick(handleSubmit); };
  }, [repType, canSubmit, submitting, handleSubmit]);

  // ── Шаг 1: выбор типа представителя ─────────────────────────────────────────
  if (repType === null) {
    return (
      <div className="container" style={{ padding: '24px 16px 32px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Регистрация</h3>
        <p className="hint" style={{ margin: '0 0 24px', lineHeight: 1.45 }}>
          Выберите, кто вы. От этого зависят дальнейшие шаги.
        </p>

        <button
          onClick={() => setRepType('greenhouse')}
          style={{
            width: '100%',
            textAlign: 'left',
            border: '1.5px solid var(--tg-button)',
            borderRadius: 14,
            padding: '16px 18px',
            background: 'var(--tg-secondary-bg)',
            color: 'var(--tg-text)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>Представитель GreenHouse</div>
          <div className="hint" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
            Управляющий кофейней или точкой сети. Можно указать желаемую точку.
          </div>
        </button>

        <button
          onClick={() => setRepType('supplier')}
          style={{
            width: '100%',
            textAlign: 'left',
            border: '1.5px solid var(--tg-hint)',
            borderRadius: 14,
            padding: '16px 18px',
            background: 'var(--tg-secondary-bg)',
            color: 'var(--tg-text)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>Представитель поставщика</div>
          <div className="hint" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
            Сотрудник поставщика. Кофейня не указывается.
          </div>
        </button>

        <LinkExistingAccount />
      </div>
    );
  }

  // ── Шаг 2: форма ────────────────────────────────────────────────────────────
  const isGreenhouse = repType === 'greenhouse';

  return (
    <div className="container" style={{ padding: '16px 16px 100px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>
        {isGreenhouse ? 'Представитель GreenHouse' : 'Представитель поставщика'}
      </h3>
      <p className="hint" style={{ margin: '0 0 20px' }}>Заполните данные — менеджер проверит и одобрит заявку</p>

      {error && (
        <div style={{ background: '#ff3b3020', border: '1px solid #ff3b30', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 14, color: '#ff3b30' }}>
          {error}
        </div>
      )}

      {/* === Контактное лицо === */}
      <p style={{ margin: '0 0 14px', fontWeight: 600, fontSize: 15 }}>Контактное лицо</p>

      <div style={fieldStyle}>
        <label style={labelStyle}>Фамилия</label>
        <input
          style={inputStyle}
          placeholder="Иванов"
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          maxLength={80}
        />
      </div>

      <div style={{ height: 14 }} />

      <div style={fieldStyle}>
        <label style={labelStyle}>Имя</label>
        <input
          style={inputStyle}
          placeholder="Иван"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          maxLength={80}
        />
      </div>

      <div style={{ height: 14 }} />

      <div style={fieldStyle}>
        <label style={labelStyle}>Email</label>
        <input
          style={{ ...inputStyle, borderColor: email && !emailValid ? '#ff3b30' : 'var(--tg-hint)' }}
          placeholder="ivanov@example.com"
          value={email}
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          onChange={e => setEmail(e.target.value)}
          maxLength={255}
        />
      </div>

      <div style={{ height: 14 }} />

      <div style={fieldStyle}>
        <label style={labelStyle}>Мобильный телефон</label>
        <input
          style={{ ...inputStyle, borderColor: phone && !phoneValid ? '#ff3b30' : 'var(--tg-hint)' }}
          placeholder="+7 999 000-00-00"
          value={phone}
          inputMode="tel"
          onChange={e => setPhone(e.target.value)}
          maxLength={32}
        />
      </div>

      {/* === Пароль для входа на сайт (резервный канал, Этап W1) === */}
      <div style={{ height: 14 }} />

      <div style={fieldStyle}>
        <label style={labelStyle}>Пароль для входа на сайт</label>
        <input
          style={{ ...inputStyle, borderColor: password && !passwordValid ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password"
          placeholder="Минимум 8 символов"
          value={password}
          autoComplete="new-password"
          onChange={e => setPassword(e.target.value)}
          maxLength={128}
        />
        <span className="hint" style={{ fontSize: 12, lineHeight: 1.4 }}>
          Понадобится, если Telegram будет недоступен — вход на сайт по e-mail и паролю.
        </span>
      </div>

      <div style={{ height: 14 }} />

      <div style={fieldStyle}>
        <label style={labelStyle}>Повторите пароль</label>
        <input
          style={{ ...inputStyle, borderColor: password2 && !passwordsMatch ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password"
          placeholder="Ещё раз тот же пароль"
          value={password2}
          autoComplete="new-password"
          onChange={e => setPassword2(e.target.value)}
          maxLength={128}
        />
        {password2 && !passwordsMatch && (
          <span style={{ fontSize: 12, color: '#ff3b30' }}>Пароли не совпадают</span>
        )}
      </div>

      {/* === Ваша точка — только для представителя GreenHouse === */}
      {isGreenhouse && (
        <>
          <div style={{ height: 20 }} />
          <div style={{ borderTop: '1px solid var(--tg-hint)', opacity: 0.2, marginBottom: 20 }} />
          <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 15 }}>Ваша точка (необязательно)</p>
          <p className="hint" style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.4 }}>
            Если вы управляете кофейней — выберите её из справочника или опишите словами.
            Это пожелание: менеджер закрепит точку за вами при одобрении заявки.
          </p>

          {shopsLoading ? (
            <p className="hint">Загрузка списка кофеен…</p>
          ) : shopsError ? (
            <p className="hint" style={{ color: '#ff3b30' }}>Не удалось загрузить список: {shopsError}</p>
          ) : selectedShop ? (
            <div
              style={{
                border: '1.5px solid var(--tg-button)',
                borderRadius: 12,
                padding: '12px 14px',
                background: 'var(--tg-secondary-bg)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{selectedShop.name}</span>
                <button
                  onClick={() => { setSelectedShopId(null); setSearch(''); }}
                  style={{ border: 'none', background: 'transparent', color: 'var(--tg-button)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                >
                  Изменить
                </button>
              </div>
              <div className="hint" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>{selectedShop.address}</div>
              <div className="hint" style={{ fontSize: 12, marginTop: 6 }}>
                {DELIVERY_LABEL[selectedShop.delivery_type] ?? selectedShop.delivery_type}
              </div>
            </div>
          ) : (
            <>
              <input
                style={inputStyle}
                placeholder="Поиск по названию или адресу"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div style={{ height: 10 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                {filteredShops.length === 0 ? (
                  <p className="hint" style={{ fontSize: 13 }}>Ничего не найдено. Попробуйте изменить запрос или опишите точку ниже.</p>
                ) : (
                  filteredShops.map(s => (
                    <button
                      key={s.id}
                      onClick={() => { setSelectedShopId(s.id); setWishText(''); }}
                      style={{
                        textAlign: 'left',
                        border: '1px solid var(--tg-hint)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                      <div className="hint" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.35 }}>{s.address}</div>
                    </button>
                  ))
                )}
              </div>
              {shops.length > filteredShops.length && (
                <p className="hint" style={{ fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                  Показаны первые {filteredShops.length}. Уточните поиск, чтобы увидеть остальные.
                </p>
              )}

              <div style={{ height: 16 }} />
              <div style={fieldStyle}>
                <label style={labelStyle}>Не нашли свою точку? Опишите её</label>
                <input
                  style={inputStyle}
                  placeholder="Название и адрес кофейни"
                  value={wishText}
                  onChange={e => setWishText(e.target.value)}
                  maxLength={500}
                />
              </div>
            </>
          )}
        </>
      )}

      <div style={{ height: 24 }} />

      {/* === Согласие на обработку ПДн (152-ФЗ) === */}
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: 'pointer',
          fontSize: 13,
          lineHeight: 1.45,
          color: 'var(--tg-text)',
          marginBottom: 16,
        }}
      >
        <input
          type="checkbox"
          checked={privacyAccepted}
          onChange={e => setPrivacyAccepted(e.target.checked)}
          style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1, accentColor: 'var(--tg-button)' }}
        />
        <span>
          Я ознакомлен(а) с{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--tg-link, #2481cc)', textDecoration: 'underline' }}
          >
            Политикой обработки персональных данных
          </a>{' '}
          и даю согласие на обработку моих персональных данных.
        </span>
      </label>

      {/* === Видимая кнопка отправки + подсказка о пропусках === */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        style={{
          width: '100%',
          padding: '14px 0',
          border: 'none',
          borderRadius: 12,
          background: canSubmit && !submitting ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
          color: canSubmit && !submitting ? 'var(--tg-button-text)' : 'var(--tg-hint)',
          fontSize: 15,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
          transition: 'background 0.15s',
        }}
      >
        {submitting ? 'Отправка…' : (canSubmit ? 'Отправить заявку' : 'Заполните поля выше')}
      </button>

      {(() => {
        if (canSubmit || submitting) return null;
        const missing: string[] = [];
        if (!lastName.trim()) missing.push('Фамилия');
        if (!firstName.trim()) missing.push('Имя');
        if (!emailValid) missing.push('Email');
        if (!phoneValid) missing.push('Мобильный телефон');
        if (!passwordValid) missing.push('Пароль (мин. 8 символов)');
        else if (!passwordsMatch) missing.push('Повтор пароля');
        if (!privacyAccepted) missing.push('Согласие на обработку ПДн');
        if (missing.length === 0) return null;
        return (
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--tg-hint)', textAlign: 'center', lineHeight: 1.4 }}>
            Не заполнено: {missing.join(', ')}
          </div>
        );
      })()}
    </div>
  );
}

// ── Привязка Telegram к аккаунту, созданному на сайте (миграция 021) ────────
// Пользователь уже регистрировался на сайте по e-mail: вместо второй анкеты вводит
// код привязки (его выдаёт сайт: кнопка «Подключить Telegram»). Backend переносит
// telegram_id в веб-аккаунт и отдаёт токен для него; Mini App перезагружается и
// дальше работает с тем же аккаунтом, депозитом и заказами.
function LinkExistingAccount() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const value = code.trim();
    if (value.length < 4) { setError('Введите код из 8 символов, например ABCD-EFGH.'); return; }
    setBusy(true);
    setError(null);
    try {
      await claimTelegramLink(value);
      window.location.reload();
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg.replace(/^\d{3}:\s*/, '').replace(/^\{"detail":"(.*)"\}$/, '$1'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          marginTop: 18,
          background: 'transparent',
          border: 'none',
          color: 'var(--tg-link)',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        У меня уже есть аккаунт на сайте → ввести код привязки
      </button>
    );
  }

  return (
    <div style={{ marginTop: 18, background: 'var(--tg-secondary-bg)', borderRadius: 14, padding: '14px 16px' }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>Код привязки с сайта</div>
      <p className="hint" style={{ margin: '4px 0 10px', lineHeight: 1.4 }}>
        На сайте greenhouse.espods.ru откройте «Уведомления» → «Подключить Telegram» и введите показанный код.
      </p>
      <input
        value={code}
        onChange={e => setCode(e.target.value.toUpperCase())}
        placeholder="ABCD-EFGH"
        autoCapitalize="characters"
        autoCorrect="off"
        style={{ ...inputStyle, letterSpacing: 2, fontWeight: 700, textAlign: 'center' }}
      />
      {error && <div style={{ color: '#ff3b30', fontSize: 13, marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          onClick={submit}
          disabled={busy}
          style={{
            flex: 1,
            background: 'var(--tg-button)',
            color: 'var(--tg-button-text)',
            border: 'none',
            borderRadius: 10,
            padding: '11px 0',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Привязываем…' : 'Привязать'}
        </button>
        <button
          onClick={() => { setOpen(false); setError(null); }}
          disabled={busy}
          style={{
            background: 'transparent',
            color: 'var(--tg-hint)',
            border: '1px solid var(--tg-hint)',
            borderRadius: 10,
            padding: '11px 14px',
            fontSize: 14,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
}
